import { useState } from "react";

import { CloseButton, Dialog, List, Portal, VStack } from "@chakra-ui/react";
import { useFormSubmitErrors } from "@features/errors/form-errors.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { z } from "zod";

import { totpCodeSchema } from "@shared/libs/validators";
import { useFormatErrorMessages } from "@shared/libs/validators/hooks/use-format-error-messages";
import { CheckboxField, Typography } from "@shared/ui/chakra";
import { Button } from "@shared/ui/chakra/buttons/button";
import { InputField } from "@shared/ui/chakra/forms/input-field";

import { totpFormMessages } from "./messages";

export const totpSchema = z.object({
  code: totpCodeSchema,
});

export type TotpFormValues = z.infer<typeof totpSchema>;

type Props = {
  onReset?: () => Promise<void>;
  onSubmit: (data: TotpFormValues) => Promise<void>;
};

export const TotpForm = ({ onReset, onSubmit }: Props) => {
  const { formatMessage } = useIntl();
  const [canReset, setCanReset] = useState(false);
  const [isResetPending, setIsResetPending] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const {
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    register,
    reset,
    setValue,
  } = useForm<TotpFormValues>({
    defaultValues: {
      code: "",
    },
    mode: "onTouched",
    resolver: zodResolver(totpSchema),
  });

  const handleReset = async () => {
    if (onReset === undefined) {
      return;
    }
    setIsResetPending(true);
    try {
      await onReset();
      setIsResetDialogOpen(false);
    } finally {
      setIsResetPending(false);
    }
  };

  const handleSubmitWithErrors = useFormSubmitErrors(
    onSubmit,
    ["ErrUsedTotp", "ErrIncorrectTOTPCode"],
    {
      ErrIncorrectTOTPCode: () => reset(),
      ErrUsedTotp: () => reset(),
    },
  );
  const formatErrorMessage = useFormatErrorMessages();

  const handleClickPaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setValue("code", text.trim(), { shouldValidate: true });
      }
    } catch (err) {
      console.error(formatMessage(totpFormMessages.clipboardError), err);
    }
  };

  return (
    <form noValidate onSubmit={handleSubmit(handleSubmitWithErrors)}>
      <VStack alignItems="stretch" gap="4" w="full">
        <InputField
          actionEl={
            <Button size="xs" variant="plain" onClick={handleClickPaste}>
              {formatMessage(totpFormMessages.pasteButton)}
            </Button>
          }
          actionOffset={90}
          errorText={formatErrorMessage(errors.code?.message)}
          input={{ ...register("code"), autoComplete: "off" }}
          label={formatMessage(totpFormMessages.codeLabel)}
          placeholder={formatMessage(totpFormMessages.codePlaceholder)}
          isInvalid={Boolean(errors.code?.message)}
        >
          {onReset && (
            <Dialog.Root
              open={isResetDialogOpen}
              placement="center"
              onOpenChange={(e) => setIsResetDialogOpen(e.open)}
            >
              <Dialog.Trigger asChild>
                <Button variant="empty-link">
                  {formatMessage(totpFormMessages.noAccessLink)}
                </Button>
              </Dialog.Trigger>
              <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                  <Dialog.Content>
                    <Dialog.Header>
                      <Dialog.Title>
                        {formatMessage(totpFormMessages.noAccessDialogTitle)}
                      </Dialog.Title>
                    </Dialog.Header>
                    <Dialog.Body>
                      <Typography color="text.fg_muted" textStyle="body2">
                        {formatMessage(totpFormMessages.noAccessDialogBody)}
                      </Typography>
                    </Dialog.Body>
                    <Dialog.Footer>
                      <Dialog.Root placement="center">
                        <Dialog.Trigger asChild>
                          <Button size="md" variant="plain">
                            {formatMessage(totpFormMessages.resetButton)}
                          </Button>
                        </Dialog.Trigger>
                        <Portal>
                          <Dialog.Backdrop />
                          <Dialog.Positioner>
                            <Dialog.Content>
                              <Dialog.Header>
                                <Dialog.Title>
                                  {formatMessage(
                                    totpFormMessages.resetConfirmDialogTitle,
                                  )}
                                </Dialog.Title>
                              </Dialog.Header>
                              <Dialog.Body>
                                <VStack alignItems="flex-start" pb="4">
                                  <Typography>
                                    {formatMessage(
                                      totpFormMessages.resetConfirmDialogBody,
                                    )}
                                  </Typography>
                                  <Typography>
                                    <FormattedMessage
                                      {...totpFormMessages.resetConfirmDialogInstructions}
                                      values={{
                                        b: (chunks) => (
                                          <Typography
                                            as="span"
                                            fontWeight="bold"
                                          >
                                            {chunks}
                                          </Typography>
                                        ),
                                      }}
                                    />
                                  </Typography>
                                  <List.Root gap="2" pl="5">
                                    <List.Item>
                                      {formatMessage(
                                        totpFormMessages.resetConfirmDialogStep1,
                                      )}
                                    </List.Item>
                                    <List.Item>
                                      {formatMessage(
                                        totpFormMessages.resetConfirmDialogStep2,
                                      )}
                                    </List.Item>
                                    <List.Item>
                                      <FormattedMessage
                                        {...totpFormMessages.resetConfirmDialogStep3}
                                        values={{
                                          b: (chunks) => (
                                            <Typography
                                              as="span"
                                              fontWeight="bold"
                                            >
                                              {chunks}
                                            </Typography>
                                          ),
                                        }}
                                      />
                                    </List.Item>
                                  </List.Root>
                                </VStack>
                                <CheckboxField
                                  label={
                                    <FormattedMessage
                                      {...totpFormMessages.resetConfirmDialogCheckbox}
                                    />
                                  }
                                  value={canReset}
                                  onChange={() =>
                                    setCanReset((prevState) => !prevState)
                                  }
                                />
                              </Dialog.Body>
                              <Dialog.Footer>
                                <Dialog.ActionTrigger asChild>
                                  <Button size="md" variant="plain">
                                    {formatMessage(
                                      totpFormMessages.cancelButton,
                                    )}
                                  </Button>
                                </Dialog.ActionTrigger>
                                <Button
                                  disabled={!canReset}
                                  loading={isResetPending}
                                  size="md"
                                  onClick={handleReset}
                                >
                                  {formatMessage(
                                    totpFormMessages.confirmButton,
                                  )}
                                </Button>
                              </Dialog.Footer>
                              <Dialog.CloseTrigger asChild>
                                <CloseButton size="sm" />
                              </Dialog.CloseTrigger>
                            </Dialog.Content>
                          </Dialog.Positioner>
                        </Portal>
                      </Dialog.Root>
                    </Dialog.Footer>
                    <Dialog.CloseTrigger asChild>
                      <CloseButton size="sm" />
                    </Dialog.CloseTrigger>
                  </Dialog.Content>
                </Dialog.Positioner>
              </Portal>
            </Dialog.Root>
          )}
        </InputField>

        <Button
          disabled={!isValid || isSubmitting}
          loading={isSubmitting}
          mt="2.5"
          type="submit"
        >
          {formatMessage(totpFormMessages.submitButton)}
        </Button>
      </VStack>
    </form>
  );
};
